import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { ConfigAlcadaNivel } from './models';
import workflowService from './workflowService';
import CampoJustificativaSox from './CampoJustificativaSox';
import { useJustificativaSugerida } from './useJustificativaSugerida';
import { extrairMensagemErro } from './mensagemErro';
import {
  FormAlcada, formDaAlcada, justificativaAlcadaEdicao, justificativaSlaEtapa, pedidoAtualizarAlcada, validarFormAlcada, validarSlaEtapa,
} from './configuracoes';

type Etapa = { etapaId: number; nomeEtapa: string; ordemSequencial: number; descricao?: string; slaDiasUteis: number | null };

const money = (v: number | null) => (v == null ? 'Sem limite' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v));

/**
 * Configurações do Workflow — alçadas por nível e SLA das etapas, lidos e gravados no backend (cada alteração exige
 * justificativa SOX e gera registro). Nada é fixo na tela: o que aparece é o que está configurado no banco.
 */
const ConfiguracoesPage: React.FC = () => {
  const history = useHistory();
  const [alcadas, setAlcadas] = useState<ConfigAlcadaNivel[]>([]);
  const [etapas, setEtapas] = useState<Etapa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [aviso, setAviso] = useState<{ tipo: 'ok' | 'erro'; texto: string } | null>(null);

  const [editAlcada, setEditAlcada] = useState<ConfigAlcadaNivel | null>(null);
  const [formA, setFormA] = useState<Omit<FormAlcada, 'justificativa'>>({ min: '', max: '', sla: '' });
  const justA = useJustificativaSugerida(editAlcada ? justificativaAlcadaEdicao(editAlcada) : '');

  const [editEtapa, setEditEtapa] = useState<Etapa | null>(null);
  const [slaEtapa, setSlaEtapa] = useState('');
  const justE = useJustificativaSugerida(editEtapa ? justificativaSlaEtapa(editEtapa.nomeEtapa, editEtapa.slaDiasUteis, slaEtapa) : '');
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [a, e] = await Promise.all([workflowService.listarAlcadas(), workflowService.listarEtapasSla()]);
      setAlcadas((Array.isArray(a) ? a : []).slice().sort((x, y) => x.nivel - y.nivel));
      setEtapas((Array.isArray(e) ? e : []).slice().sort((x, y) => x.ordemSequencial - y.ordemSequencial));
    } catch (err) {
      setAviso({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível carregar as configurações.') });
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirAlcada = (a: ConfigAlcadaNivel) => {
    const f = formDaAlcada(a);
    setFormA({ min: f.min, max: f.max, sla: f.sla });
    justA.restaurar();
    setAviso(null);
    setEditAlcada(a);
  };

  const salvarAlcada = async () => {
    if (!editAlcada) return;
    const form: FormAlcada = { ...formA, justificativa: justA.texto };
    const erro = validarFormAlcada(form);
    if (erro) { setAviso({ tipo: 'erro', texto: erro }); return; }
    setSalvando(true);
    try {
      await workflowService.atualizarAlcada(editAlcada.id, pedidoAtualizarAlcada(editAlcada, form));
      setEditAlcada(null);
      setAviso({ tipo: 'ok', texto: `Alçada N${editAlcada.nivel} atualizada. Registro SOX gerado.` });
      await carregar();
    } catch (err) {
      setAviso({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível atualizar a alçada.') });
    } finally {
      setSalvando(false);
    }
  };

  const abrirEtapa = (e: Etapa) => {
    setSlaEtapa(e.slaDiasUteis != null ? String(e.slaDiasUteis) : '');
    justE.restaurar();
    setAviso(null);
    setEditEtapa(e);
  };

  const salvarEtapa = async () => {
    if (!editEtapa) return;
    const erro = validarSlaEtapa(slaEtapa, justE.texto);
    if (erro) { setAviso({ tipo: 'erro', texto: erro }); return; }
    setSalvando(true);
    try {
      await workflowService.atualizarSlaEtapa(editEtapa.etapaId, Number(slaEtapa), justE.texto.trim());
      setEditEtapa(null);
      setAviso({ tipo: 'ok', texto: `SLA da etapa "${editEtapa.nomeEtapa}" atualizado. Registro SOX gerado.` });
      await carregar();
    } catch (err) {
      setAviso({ tipo: 'erro', texto: extrairMensagemErro(err, 'Não foi possível atualizar o SLA da etapa.') });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className='wf-page'>
      <div className='wf-header'>
        <div><h1>Configurações do Workflow SOX</h1><p>Alçadas financeiras por nível e SLA das etapas do fluxo, direto do que está configurado.</p></div>
        <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>
      </div>

      {aviso && (
        <div className={aviso.tipo === 'ok' ? 'wf-toast wf-toast--success' : 'wf-review'} role={aviso.tipo === 'erro' ? 'alert' : undefined}>
          {aviso.tipo === 'erro' && <strong>Atenção</strong>}<p style={{ margin: 0 }}>{aviso.texto}</p>
        </div>
      )}
      {carregando && <p className='wf-count'>Carregando...</p>}

      <section className='wf-card'>
        <div className='wf-card__header'><div><h2>Alçadas financeiras e SLA</h2><p>N7 é autoridade máxima de governança — não aprova valores financeiros.</p></div></div>
        {!carregando && alcadas.length === 0 ? <p className='wf-count'>Nenhuma alçada configurada.</p> : (
          <div className='wf-table-wrap'><table className='wf-table'>
            <thead><tr><th>Nível</th><th>Cargo</th><th>Escopo</th><th>Valor mínimo</th><th>Valor máximo</th><th>SLA</th><th>Titular</th><th /></tr></thead>
            <tbody>{alcadas.map(a => (
              <tr key={a.id}>
                <td><strong>N{a.nivel}</strong></td><td>{a.cargo}</td><td>{a.scopeLabel || a.scopeType}</td>
                <td>{money(a.valorMin)}</td><td>{money(a.valorMax)}</td><td>{a.slaDiasUteis} dia(s) útil(eis)</td>
                <td>{a.titular && a.titular !== '—' ? a.titular : <span className='wf-danger-text'>Sem titular</span>}</td>
                <td><button className='wf-link' onClick={() => abrirAlcada(a)}>Editar</button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </section>

      <section className='wf-card' style={{ marginTop: 20 }}>
        <div className='wf-card__header'><div><h2>Etapas do fluxo</h2><p>SLA em dias úteis por etapa. A etapa Aprovação segue a escada de alçadas acima.</p></div></div>
        {!carregando && etapas.length === 0 ? <p className='wf-count'>Nenhuma etapa cadastrada.</p> : (
          <div className='wf-config-steps'>{etapas.map(e => (
            <div key={e.etapaId}>
              <span>{e.ordemSequencial}</span>
              <div><strong>{e.nomeEtapa}</strong><small>{e.descricao ? `${e.descricao} | ` : ''}SLA: {e.slaDiasUteis != null ? `${e.slaDiasUteis} dia(s) útil(eis)` : 'pela alçada'}</small></div>
              {e.nomeEtapa !== 'Aprovação' && <button className='wf-link' onClick={() => abrirEtapa(e)}>Configurar</button>}
            </div>
          ))}</div>
        )}
      </section>

      {editAlcada && (
        <div className='wf-modal-bg'><div className='wf-modal'>
          <div className='wf-modal__header'><div><h2>Editar alçada — N{editAlcada.nivel}</h2><p>{editAlcada.cargo} — {editAlcada.scopeLabel || editAlcada.scopeType}</p></div><button onClick={() => setEditAlcada(null)} aria-label='Fechar'>×</button></div>
          <div className='wf-modal__body'>
            <div className='wf-form-grid'>
              <label>Valor mínimo (R$)<input value={formA.min} onChange={e => { const v = e.target.value; setFormA(f => ({ ...f, min: v })); }} aria-label='Valor mínimo' /></label>
              <label>Valor máximo (R$) <small>(vazio = sem limite)</small><input value={formA.max} onChange={e => { const v = e.target.value; setFormA(f => ({ ...f, max: v })); }} aria-label='Valor máximo' /></label>
              <label>SLA (dias úteis)<input value={formA.sla} onChange={e => { const v = e.target.value; setFormA(f => ({ ...f, sla: v })); }} aria-label='SLA em dias úteis' /></label>
              <label style={{ gridColumn: '1/-1' }}>Justificativa SOX <span style={{ color: '#f64e60', fontSize: 10 }}>(obrigatória)</span>
                <CampoJustificativaSox justificativa={justA} rows={3} placeholder='Motivo do ajuste da alçada...' />
              </label>
            </div>
          </div>
          <div className='wf-modal__footer'>
            <button className='wf-btn wf-btn--secondary' onClick={() => setEditAlcada(null)} disabled={salvando}>Cancelar</button>
            <button className='wf-btn wf-btn--primary' onClick={salvarAlcada} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </div></div>
      )}

      {editEtapa && (
        <div className='wf-modal-bg'><div className='wf-modal'>
          <div className='wf-modal__header'><div><h2>Configurar etapa — {editEtapa.nomeEtapa}</h2><p>SLA recomendado em dias úteis</p></div><button onClick={() => setEditEtapa(null)} aria-label='Fechar'>×</button></div>
          <div className='wf-modal__body'>
            <div className='wf-form-grid wf-form-grid--one'>
              <label>SLA (dias úteis)<input value={slaEtapa} onChange={e => { const v = e.target.value; setSlaEtapa(v); }} aria-label='SLA da etapa' /></label>
              <label>Justificativa SOX <span style={{ color: '#f64e60', fontSize: 10 }}>(obrigatória)</span>
                <CampoJustificativaSox justificativa={justE} rows={3} placeholder='Motivo do ajuste do SLA...' />
              </label>
            </div>
          </div>
          <div className='wf-modal__footer'>
            <button className='wf-btn wf-btn--secondary' onClick={() => setEditEtapa(null)} disabled={salvando}>Cancelar</button>
            <button className='wf-btn wf-btn--primary' onClick={salvarEtapa} disabled={salvando}>{salvando ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </div></div>
      )}
    </div>
  );
};

export default ConfiguracoesPage;
